// #tag: USER LOGIN SCREEN
// #purpose: Authenticates users via mobile phone OTP, Google single sign-on, or email/password credentials.
import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { Illustration } from '../components/common/Illustration';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { PasswordField } from '../components/auth/PasswordField';
import { useAuth } from '../context/AuthProvider';
import {
  completePhoneSignIn,
  formatFirebasePhoneAuthError,
  startPhoneSignIn,
} from '../lib/firebase/phoneAuth';
import { getPostLoginPath } from '../lib/authRedirect';
import type { ConfirmationResult } from 'firebase/auth';

function postLoginPath(from?: string, role?: string | null) {
  return getPostLoginPath(from, role);
}

export function LoginScreen() {
  const { loginWithPhone, login, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  const [mobile, setMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpConfirmation, setOtpConfirmation] = useState<{ mode: 'web'; confirmation: ConfirmationResult } | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSendOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setOtpLoading(true);
    try {
      const result = await startPhoneSignIn(mobile);
      setOtpConfirmation(result);
      setSuccess(`Verification code sent to ${mobile.trim().length === 10 ? `+91${mobile.trim()}` : mobile.trim()}`);
    } catch (err: unknown) {
      setError(formatFirebasePhoneAuthError(err));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!otpConfirmation) return;
    setError(null);
    setOtpLoading(true);
    try {
      const idToken = await completePhoneSignIn(otpConfirmation, otpCode);
      const me = await loginWithPhone(idToken);
      navigate(postLoginPath(from, me.role), { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid verification code.');
    } finally {
      setOtpLoading(false);
    }
  };

  const onSubmitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setOtpLoading(true);
    try {
      const me = await login(email, password);
      navigate(postLoginPath(from, me.role), { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setOtpLoading(false);
    }
  };

  if (status === 'authenticated' && user) {
    return <Navigate to={postLoginPath(from, user.role)} replace />;
  }

  return (
    <section className="adaptive-page auth-screen">
      <div className="auth-layout">
        <Illustration context="onboarding.auth" size="md" className="auth-screen-illustration" />
        <div className="auth-card glass-surface">
          <span className="section-kicker">Sign in</span>
          <h1>Your exam journey starts here</h1>
          <p>Save exams, track notifications, and manage your document vault.</p>

          {error && <div className="auth-error" role="alert">{error}</div>}
          {success && <div className="auth-success" role="status">{success}</div>}

          {!showEmailLogin ? (
            !otpConfirmation ? (
              <form className="auth-form" onSubmit={handleSendOtp}>
                <label className="auth-field">
                  <Phone size={17} aria-hidden="true" />
                  <input
                    type="tel"
                    inputMode="tel"
                    placeholder="Mobile number"
                    autoComplete="tel"
                    required
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                  />
                </label>
                <div id="recaptcha-container" />
                <button type="submit" className="primary-action" disabled={otpLoading}>
                  {otpLoading ? 'Sending code…' : 'Send OTP'}
                </button>
              </form>
            ) : (
              <form className="auth-form" onSubmit={handleVerifyOtp}>
                <label className="auth-field">
                  <Phone size={17} aria-hidden="true" />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter 6-digit OTP"
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                  />
                </label>
                <button type="submit" className="primary-action" disabled={otpLoading}>
                  {otpLoading ? 'Verifying…' : 'Verify & sign in'}
                </button>
                <button
                  type="button"
                  className="secondary-action compact"
                  onClick={() => { setOtpConfirmation(null); setOtpCode(''); }}
                >
                  Change number
                </button>
              </form>
            )
          ) : (
            <form className="auth-form" onSubmit={onSubmitEmail}>
              <label className="auth-field">
                <Mail size={17} aria-hidden="true" />
                <input
                  type="email"
                  placeholder="Email address"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <PasswordField
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="Password"
                autoComplete="current-password"
              />
              <button type="submit" className="primary-action" disabled={otpLoading}>
                {otpLoading ? 'Signing in…' : 'Sign in with email'}
              </button>
            </form>
          )}

          <GoogleSignInButton onSignedIn={(signedInUser) => navigate(postLoginPath(from, signedInUser.role), { replace: true })} />

          <div className="auth-links">
            <button type="button" className="text-action" onClick={() => setShowEmailLogin((v) => !v)}>
              {showEmailLogin ? 'Use phone OTP instead' : 'Sign in with email instead'}
            </button>
            <span>
              New here? <Link to="/register" state={{ from }}>Create an account</Link>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
